import { ActionIcon, Grid, TextInput, Tooltip } from '@mantine/core';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import {
  IconBarcode,
  IconBarcodeOff,
  IconEraser,
  IconSearch
} from '@tabler/icons-react';
import { ISection } from '../../../interfaces/Section';
import { IProduct } from '../../../interfaces/Product';
import ProductType from '../../../enum/ProductType';
import { useFormContext } from 'react-hook-form';
import { OrderProduct } from '../interfaces';
import { OrderContext } from '../../../context/OrderContext';
import {
  findMergeableCatalogLineIndex,
  generateObjectIdLikeLineId
} from '../utils/lineIdentity';
import { initializePieceCountForNewLine, maybeRecomputePieceCount } from '../utils/pieceCount';
import { recalculateOrderProductsWithLineIds } from '../utils/recalculateOrderProducts';

interface ProductSearcherProps {
  searchTerm: string;
  onSearch: (term: string) => void;
  sections: ISection[];
  storeDiscounts: any;
}

const ProductSearcher = ({
  searchTerm,
  onSearch,
  sections,
  storeDiscounts
}: ProductSearcherProps) => {
  const { t } = useTranslation();
  const productSearchTermInputRef = useRef<HTMLInputElement>(null);
  const scannerInputRef = useRef<HTMLInputElement>(null);
  const [scannerActive, setScannerActive] = useState(false);
  const { watch, setValue, getValues } = useFormContext();
  const { setLastAddedOrderProduct, setUpdatingProductModal, setUpdatingProduct } =
    useContext(OrderContext);

  const products = watch('products');

  const allProductsBarcodesMap = useMemo(() => {
    if (!sections) return {};

    return sections.reduce<Record<string, IProduct>>((barcodesMap, section) => {
      const products = section.products || [];

      products.forEach((product) => {
        const typedProduct = product as IProduct;
        if (typedProduct.barcode) {
          barcodesMap[typedProduct.barcode] = typedProduct;
        }
      });

      return barcodesMap;
    }, {});
  }, [sections]);


  //TODO: THIS IS ALREADY ON PRODUCT LIST COMPONENT, FIND A WAY TO REUSE IT
  const getLatestValues = () => ({
    discountCodeObj: getValues('discountCodeObj'),
    express: getValues('express'),
    customerInfo: getValues('customerInfo'),
    orderType: getValues('orderType'),
    returnItemsChargeMode: getValues('returnItemsChargeMode')
  });

  const updateOrderProducts = useCallback(
    (newProducts: OrderProduct[]) => {
      // Get latest values at the time of update
      const { discountCodeObj, express, customerInfo, orderType, returnItemsChargeMode } =
        getLatestValues();
      const { newOrderProds, buyAndGetProds } = recalculateOrderProductsWithLineIds({
        discountCodeObj,
        products: newProducts,
        express,
        storeDiscounts,
        customerInfo,
        previousBuyAndGetProducts: getValues('buyAndGetProducts'),
        orderType,
        returnItemsChargeMode
      });
      setValue('products', newOrderProds);
      setValue('buyAndGetProducts', buyAndGetProds);
    },
    [storeDiscounts, setValue, getValues]
  );

  const onProductBarcodeScanned = (product: IProduct, quantity: number) => {
    const isMeasuredProduct =
      product.type === ProductType.WEIGHT || product.type === ProductType.LENGTH;
    if (isMeasuredProduct) {
      const lineId = generateObjectIdLikeLineId();
      setUpdatingProductModal(true);
      setUpdatingProduct(initializePieceCountForNewLine({
        ...product,
        lineId,
        orderProductLineID: lineId,
        productId: product._id || '',
        quantity: 0,
        extraAmount: 0,
        discountAmount: 0,
        properties: [],
        attributes: []
      }));
      return;
    }

    // merge for normal products by productId + selectedOptionId
    const prodIdx = findMergeableCatalogLineIndex(products || [], product._id || '', null);

    if (prodIdx === -1) {
      //PRODUCT NOT FOUND IN ORDER
      const lineId = generateObjectIdLikeLineId();
      const newProduct: OrderProduct = initializePieceCountForNewLine({
        ...product,
        lineId,
        orderProductLineID: lineId,
        productId: product._id,
        selectedOptionId: null,
        extraAmount: 0,
        discountAmount: 0,
        properties: [],
        quantity: 1,
        price: product.price,
        name: product.name,
        expressPrice: product.expressPrice,
        type: product.type,
        taxExemptOne: product.taxExemptOne,
        taxExemptTwo: product.taxExemptTwo,
        taxExemptThree: product.taxExemptThree,
        attributes: [{}]
      });
      const productsCopy = [...(products || []), newProduct];
      setLastAddedOrderProduct(newProduct);
      updateOrderProducts(productsCopy);
    } else {
      const productsCopy = [...(products || [])];
      productsCopy[prodIdx] = maybeRecomputePieceCount({
        ...productsCopy[prodIdx],
        quantity: (productsCopy[prodIdx].quantity || 0) + 1,
        attributes: productsCopy[prodIdx].attributes
          ? [...productsCopy[prodIdx].attributes, {}]
          : [{}]
      });
      setLastAddedOrderProduct(productsCopy[prodIdx]);
      updateOrderProducts(productsCopy);
    }
  };

  const onBarcodeScanned = async (barcode: string) => {
    const product = allProductsBarcodesMap[barcode];
    if (product) {
      await onProductBarcodeScanned(product, 1);
    }
  };

  // Debounced function that will be called 500ms after the last keystroke
  const handleSearchDebounced = debounce((value: string) => {
    onSearch(value);
  }, 500);

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (
        scannerInputRef.current &&
        e.target instanceof HTMLElement &&
        e.target !== scannerInputRef.current
      ) {
        setScannerActive(false); // auto-disable scanner
      }
    };

    window.addEventListener('focusin', handleFocus);
    return () => window.removeEventListener('focusin', handleFocus);
  }, []);

  return (
    <Grid align="center" justify="center">
      <Grid.Col span={{ xs: 12, md: 11 }}>
        <TextInput
          ref={productSearchTermInputRef}
          defaultValue={searchTerm}
          onChange={(event) => handleSearchDebounced(event.currentTarget.value)}
          placeholder={t('FieldName:product')}
          rightSection={
            productSearchTermInputRef.current?.value ? (
              <IconEraser
                style={{ cursor: 'pointer' }}
                color="var(--mantine-color-washday-primary-9)"
                onClick={() => {
                  if (productSearchTermInputRef.current) {
                    productSearchTermInputRef.current.value = '';
                  }
                  handleSearchDebounced('');
                }}
                size={16}
              />
            ) : (
              <IconSearch size={16} />
            )
          }
        />
      </Grid.Col>
      <Grid.Col span={{ xs: 12, md: 1 }}>
        <Tooltip
          label={
            scannerActive
              ? t('CommonButtons:disableScanner') // Ej: "Desactivar escáner"
              : t('CommonButtons:enableScanner') // Ej: "Activar escáner"
          }>
          <ActionIcon
            variant={scannerActive ? 'filled' : 'light'}
            color={scannerActive ? 'blue' : 'gray'}
            size="lg"
            w={'100%'}
            aria-label={
              scannerActive ? t('CommonButtons:disableScanner') : t('CommonButtons:enableScanner')
            }
            onClick={() => {
              setScannerActive(!scannerActive);
              if (!scannerActive) {
                setTimeout(() => scannerInputRef.current?.focus(), 100); // Reactiva focus al reactivar
              }
            }}>
            {scannerActive ? <IconBarcode size={20} /> : <IconBarcodeOff size={20} />}
          </ActionIcon>
        </Tooltip>
      </Grid.Col>
      <Grid.Col span={{ xs: 12, md: 1 }}>
        <TextInput
          ref={scannerInputRef}
          type="text"
          disabled={!scannerActive}
          onBlur={() => setScannerActive(false)}
          style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          onKeyDown={async (e) => {
            if (e.key === 'Enter') {
              const scannedCode = e.currentTarget.value.trim().toLowerCase();
              if (scannedCode) {
                e.currentTarget.value = '';
                await onBarcodeScanned(scannedCode);
              }
            }
          }}
        />
      </Grid.Col>
    </Grid>
  );
};

export default ProductSearcher;
